import type { SupabaseClient } from "@supabase/supabase-js";

import type {
  CyanOwnerDecisionRecord,
  CyanOwnerDecisionRepository,
} from "./cyanOwnerDecisionRepository";

function rowString(row: Record<string, unknown>, key: string): string {
  const value = row[key];

  if (typeof value !== "string") {
    throw new Error(`Invalid ${key} in cyan owner decision.`);
  }

  return value;
}

function rowBoolean(row: Record<string, unknown>, key: string): boolean {
  return row[key] === true;
}

/**
 * Durable Cyan operational-owner authority repository.
 *
 * Use the canonical Supabase client contract directly.
 *
 * This intentionally avoids maintaining a parallel approximation of
 * PostgREST fluent query-builder types, which can diverge from the real
 * Supabase runtime contract.
 */
export class SupabaseCyanOwnerDecisionRepository implements CyanOwnerDecisionRepository {
  constructor(private readonly db: SupabaseClient) {}

  async findByFingerprint(
    fingerprint: string,
  ): Promise<CyanOwnerDecisionRecord | undefined> {
    const { data, error } = await this.db
      .from("cyan_owner_authority_decisions")
      .select("*")
      .eq("fingerprint", fingerprint)
      .maybeSingle();

    if (error) {
      throw new Error(
        `cyan_owner_authority_decisions read failed: ${error.message}`,
      );
    }

    if (!data) {
      return undefined;
    }

    const row = data as Record<string, unknown>;

    return {
      id: rowString(row, "id"),

      fingerprint: rowString(row, "fingerprint"),

      action: rowString(row, "action"),

      scope: rowString(row, "scope"),

      risk: rowString(row, "risk") as CyanOwnerDecisionRecord["risk"],

      decision: rowString(
        row,
        "decision",
      ) as CyanOwnerDecisionRecord["decision"],

      authorized: rowBoolean(row, "authorized"),

      requiresHumanOwner: rowBoolean(row, "requires_human_owner"),

      decidedBy: "cyan-operational-owner",

      reason: rowString(row, "reason"),

      reusable: rowBoolean(row, "reusable"),

      createdAt: rowString(row, "created_at"),

      updatedAt: rowString(row, "updated_at"),
    };
  }

  async save(record: CyanOwnerDecisionRecord): Promise<void> {
    const { error } = await this.db
      .from("cyan_owner_authority_decisions")
      .upsert(
        {
          id: record.id,

          fingerprint: record.fingerprint,

          action: record.action,

          scope: record.scope,

          risk: record.risk,

          decision: record.decision,

          authorized: record.authorized,

          requires_human_owner: record.requiresHumanOwner,

          decided_by: record.decidedBy,

          reason: record.reason,

          reusable: record.reusable,

          created_at: record.createdAt,

          updated_at: record.updatedAt,
        },
        {
          onConflict: "fingerprint",
        },
      );

    if (error) {
      throw new Error(
        `cyan_owner_authority_decisions write failed: ${error.message}`,
      );
    }
  }
}
